import React from 'react';
import {
  Drawer,
  Stack,
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import FlyoutHeader from './FlyoutHeader';
import {
  FLYOUT_WIDTH,
  FLYOUT_CONTENT_PADDING_X,
  FLYOUT_CONTENT_PADDING_Y,
} from '../constants';

interface FlyoutErrorStateProps {
  open: boolean;
  onClose: () => void;
  error?: Error | unknown;
  onRetry?: () => void;
  message?: string;
  title?: string;
  subtitle?: string;
}

const FlyoutErrorState: React.FC<FlyoutErrorStateProps> = ({
  open,
  onClose,
  error,
  onRetry,
  message = 'Failed to load details',
  title = 'Error Loading',
  subtitle = 'Details',
}) => {
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'An unexpected error occurred';

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      variant='temporary'
    >
      <Stack sx={{ width: FLYOUT_WIDTH }}>
        {/* Header */}
        <FlyoutHeader
          title={title}
          subtitle={subtitle}
          onClose={onClose}
        />

        {/* Error Content */}
        <Stack
          direction='column'
          px={FLYOUT_CONTENT_PADDING_X}
          py={FLYOUT_CONTENT_PADDING_Y}
          spacing={3}
        >
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            py={4}
          >
            <ErrorIcon
              color='error'
              sx={{ fontSize: 48, mb: 2 }}
            />

            <Alert
              severity='error'
              sx={{ width: '100%', mb: 3 }}
            >
              <AlertTitle>{message}</AlertTitle>
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {errorMessage}
              </Typography>

              {process.env.NODE_ENV === 'development' &&
                error instanceof Error &&
                error.stack && (
                  <Box
                    component='pre'
                    sx={{
                      fontSize: '12px',
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      p: 1,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px',
                      mt: 1,
                    }}
                  >
                    {error.stack}
                  </Box>
                )}
            </Alert>

            <Stack
              direction='row'
              spacing={2}
            >
              {onRetry && (
                <Button
                  variant='contained'
                  startIcon={<RefreshIcon />}
                  onClick={onRetry}
                  size='small'
                >
                  Try Again
                </Button>
              )}

              <Button
                variant='outlined'
                onClick={onClose}
                size='small'
              >
                Close
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FlyoutErrorState;
